/* eslint-disable @typescript-eslint/no-misused-promises, no-var, prefer-const */
import * as React from "react";
import DialogContent from "@mui/material/DialogContent";
import { IGroundEvent, IFlight } from "@vmosolutions/oc-interfaces";
import { useState, useEffect } from "react";
import moment from "moment-timezone";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { DialogActions, useTheme } from "@mui/material";
import { FlightChipCollection } from "src/components/Flights/FlightChip";
import { ModalTitle, ModalButton, ModalMessage } from "../DialogModalComponents";
import { EventModalTypes } from "../DialogModalWrapper";
import { updateSelectedGroundEvent, updateSelectedFlight } from "src/redux/actions/flights/FlightData.Actions";
import { dateTimeFormat } from "../dateTimeFormat";
import { isFlight } from "src/redux/reducers/FlightTimelineReducer";
import { updateAirportOptions } from "src/utils/airportHelper";
import { DialogModalStyles } from "../DialogModalWrapper";
// form components
import { DateTimeField } from "src/components/FormikComponents/DateTimeField";

/**
 * Modal appears only when 1 flight is slected and the ground turnback button is selected
 * gathers user info from via modal and dispatches an API call to update flight and create a ground event
 */

interface ISetNewStartTimeModalProps {
  isOpen: boolean;
  handleClose: (value: boolean) => void;
  setActiveEventModal: (EventModalTypes: EventModalTypes) => void;
}

export const SetNewStartTimeModal = (props: ISetNewStartTimeModalProps): JSX.Element => {
  const { isOpen, handleClose, setActiveEventModal } = props;
  const dispatch = useDispatch();
  const theme = useTheme();
  const selectedEvents: (IFlight | IGroundEvent)[] = useSelector(
    (state: IRootState) => state.timelineFlights.selectedEvents
  );

  const airportOptions = useSelector((state: IRootState) => state.structures.airportOptions);

  const selectedGroundEvents: IGroundEvent[] = React.useMemo(() => {
    return selectedEvents.filter((event: IFlight | IGroundEvent) => !isFlight(event)) as IGroundEvent[];
  }, [selectedEvents]);

  const selectedFlights: IFlight[] = React.useMemo(() => {
    return selectedEvents.filter((event: IFlight | IGroundEvent) => isFlight(event));
  }, [selectedEvents]);

  const typeFlight: boolean = selectedFlights.length > 0 ? true : false;
  const oneGroundEvent: boolean = !typeFlight && selectedGroundEvents.length === 1 ? true : false;

  //select earlist event
  let earliestEvent: number;
  let airportIdent: string;

  if (typeFlight && selectedFlights.length > 0) {
    const earliestFlight: IFlight = selectedFlights.reduce((earlyFlight, flight) =>
      flight.actualOut > earlyFlight.actualOut ? earlyFlight : flight
    );
    earliestEvent = earliestFlight.actualOut?.valueOf();
    //check that there is selected flight with an orgin airport
    airportIdent =
      selectedFlights.length > 0 && selectedFlights[0].hasOwnProperty("origin") ? selectedFlights[0].origin : null;
  }
  if (!typeFlight && selectedGroundEvents.length > 0) {
    const earliestGroundEvent: IGroundEvent = selectedGroundEvents.reduce((earlyGroundEvent, groundEvent) =>
      groundEvent.actualStart > earlyGroundEvent.actualStart ? earlyGroundEvent : groundEvent
    );
    earliestEvent = earliestGroundEvent.actualStart?.valueOf();
    //check that there is selected groundevent with an location airport
    airportIdent =
      selectedGroundEvents.length > 0 && selectedGroundEvents[0].hasOwnProperty("location")
        ? selectedGroundEvents[0].location
        : null;
  }

  useEffect(() => {
    if (airportIdent) {
      updateAirportOptions(airportIdent, airportOptions, dispatch);
    }
  }, [airportIdent, airportOptions, dispatch]);

  const [timeSelected, setTimeSelected] = useState<number>(earliestEvent);
  const [endTimeSelected, setEndTimeimeSelected] = useState<number>(
    oneGroundEvent ? selectedGroundEvents[0].actualEnd?.valueOf() : earliestEvent
  );
  const [timeShift, setTimeShift] = useState<number>(null);
  const [shiftMessage, setShiftMessage] = useState<string>(null);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true);

  //////////////
  // HANDLERS //
  //////////////

  const generateMessage = (time: number): void => {
    let data = dateTimeFormat(time);
    const groupType: string = typeFlight ? "flight" : "ground event";
    if (data) {
      setShiftMessage(
        `Selection will shift ${groupType} group ${data.direction} by ${data.time.day} ${data.time.hour} ${data.time.minute}`
      );
    } else {
      setShiftMessage(`Selection will not shift ${groupType}  group`);
    }
  };

  const handleTimeChange = (event: any): void => {
    let timeRange = timeSelected;
    timeRange = event;
    setTimeSelected(timeRange);
    if (!oneGroundEvent) {
      const timeIncrement: number = timeRange - earliestEvent;
      generateMessage(timeIncrement);
      if (timeIncrement === 0) {
        setTimeShift(null);
        setButtonDisabled(true);
      } else {
        setTimeShift(timeIncrement);
        setButtonDisabled(false);
      }
    } else {
      if (moment(event).isSameOrAfter(moment(endTimeSelected))) setButtonDisabled(true);
      else setButtonDisabled(false);
    }
  };

  const handleEndTimeChange = (event: any): void => {
    setEndTimeimeSelected(event);
    if (moment(timeSelected).isSameOrAfter(moment(event))) setButtonDisabled(true);
    else setButtonDisabled(false);
  };

  const handleCancel = (): void => {
    handleClose(false);
    setActiveEventModal(EventModalTypes.None);
  };

  const handleConfirm = (): void => {
    let success = 0;
    if (typeFlight) {
      selectedFlights.forEach(async (selectedFlight: IFlight) => {
        //shift the times for flight
        const shiftedTimes = {
          scheduledOff: selectedFlight.scheduledOff?.valueOf() + timeShift,
          scheduledOn: selectedFlight.scheduledOn?.valueOf() + timeShift,
          actualOff: selectedFlight.actualOff?.valueOf() + timeShift,
          actualOn: selectedFlight.actualOn?.valueOf() + timeShift,

          //if the timeline is showing block time - thses times should be updated as well...
          scheduledOut: selectedFlight.scheduledOut?.valueOf() + timeShift,
          scheduledIn: selectedFlight.scheduledIn?.valueOf() + timeShift,
          actualOut: selectedFlight.actualOut?.valueOf() + timeShift,
          actualIn: selectedFlight.actualIn?.valueOf() + timeShift,
        };
        var result = await dispatch(updateSelectedFlight(selectedFlight._id, shiftedTimes));
        if (result.hasOwnProperty("newFlight")) {
          success++;
        }
        if (success === selectedFlights.length) {
          //wait to close modal until success...
          handleClose(false);
          setActiveEventModal(EventModalTypes.None);
        } else {
          handleClose(false);
          setActiveEventModal(EventModalTypes.None);
        }
      });
    } else if (oneGroundEvent) {
      const selectedGroundEvent = selectedGroundEvents[0];
      const data = {
        actualStart: timeSelected,
        actualEnd: endTimeSelected,
        scheduledStart: timeSelected,
        scheduledEnd: endTimeSelected,
      };
      var result = Promise.resolve(dispatch(updateSelectedGroundEvent(selectedGroundEvent._id, data)));
      if (result.hasOwnProperty("newEvent")) {
        //wait to close modal until success...
        handleClose(false);
        setActiveEventModal(EventModalTypes.None);
      } else {
        handleClose(false);
        setActiveEventModal(EventModalTypes.None);
      }
    } else {
      //for ground event
      selectedGroundEvents.forEach(async (selectedGroundEvent: IGroundEvent) => {
        const data = {
          actualStart: selectedGroundEvent.actualStart?.valueOf() + timeShift,
          actualEnd: selectedGroundEvent.actualEnd?.valueOf() + timeShift,
          scheduledStart: selectedGroundEvent.scheduledStart?.valueOf() + timeShift,
          scheduledEnd: selectedGroundEvent.scheduledEnd?.valueOf() + timeShift,
        };
        var result = await dispatch(updateSelectedGroundEvent(selectedGroundEvent._id, data));
        if (result.hasOwnProperty("newEvent")) {
          //wait to close modal until success...
          handleClose(false);
          setActiveEventModal(EventModalTypes.None);
        } else {
          handleClose(false);
          setActiveEventModal(EventModalTypes.None);
        }
      });
    }
  };

  if (isOpen && selectedEvents.length > 0) {
    return (
      <DialogContent sx={DialogModalStyles(theme)}>
        <div className="modal_content">
          {!!typeFlight && <FlightChipCollection flights={selectedFlights} disableContext={true} disableFocus={true} />}

          <ModalTitle title={typeFlight ? "Set New Start Time" : "Set Ground Event Times"}></ModalTitle>

          {!shiftMessage && <ModalMessage message={shiftMessage}></ModalMessage>}

          <DateTimeField
            className={"datetimeInput"}
            name="start_time"
            label={oneGroundEvent ? "Start" : ""}
            showAirportTime={airportIdent}
            value={timeSelected}
            onChange={handleTimeChange}
            // Only set maxDate if onGroundEvent is set and using both times...
            maxDate={oneGroundEvent ? endTimeSelected : undefined}
            isEpoch={true}
          />

          {oneGroundEvent && (
            <DateTimeField
              className={"datetimeInput"}
              name="end_time"
              label={oneGroundEvent ? "End" : ""}
              showAirportTime={airportIdent}
              value={endTimeSelected}
              onChange={handleEndTimeChange}
              minDate={timeSelected}
              isEpoch={true}
            />
          )}

          <DialogActions>
            <ModalButton label="Cancel" handler={handleCancel}></ModalButton>
            <ModalButton label="Confirm" handler={handleConfirm} disable={buttonDisabled}></ModalButton>
          </DialogActions>
        </div>
      </DialogContent>
    );
  } else {
    return null;
  }
};
